import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'interview/i18n'
import { authenticateSession, disconnectSession } from 'actions/opentok'
import TimedInterviewWrapper from 'interview/components/sequenced-interview/timed-interview-wrapper'
import { updateState } from 'interview/actions/candidate'
import {
  startTest,
  updateQualityTestData,
  updateNetworkTestData,
  updateError,
} from 'interview/actions/sequenced-interview'
import StreamView from 'interview/components/sequenced-interview/stream-view'
import TimedInterviewError from 'interview/components/sequenced-interview/timed-interview-error'
import NetworkTestResults from './network-test-results'
import { Body, H3, Subtitle } from 'components/styled/typography'
import { Confirmation } from 'components/styled/modal/confirmation'
import { Box } from 'components/styled/containers/box'
import { Button } from 'components/styled/button'
import { Flex } from 'components/styled/containers/flex'
import * as analytics from 'analytics/performance'
import styled from 'styled-components'
import { getCandidateInfoForAmplitude, getCompanyAndInterviewInfoForAmplitude } from 'selectors'
import jobOpening from 'interview/services/job-opening'

const propTypes = {
  i18n: PropTypes.shape({
    sequencedInterview: PropTypes.shape({
      browserInfo: PropTypes.shape({
        browserSupportTitle: PropTypes.string,
        android: PropTypes.string,
        iOS: PropTypes.string,
        supportedBrowsers: PropTypes.string,
        mobileSupported: PropTypes.string,
        freezeInstruction: PropTypes.string,
        latestVersion: PropTypes.string,
      }),
      networkTest: PropTypes.shape({
        startTest: PropTypes.string,
        testAgain: PropTypes.string,
        confirmWarning: PropTypes.string,
        confirmText: PropTypes.string,
        confirmText2: PropTypes.string,
        confirmText3: PropTypes.string,
        goodLuck: PropTypes.string,
        header: PropTypes.string,
        description: PropTypes.string,
        confirmTitle: PropTypes.string,
        startInterview: PropTypes.string,
      }),
    }),
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  jobOpening: PropTypes.shape({
    additionalFormFields: PropTypes.arrayOf(PropTypes.object),
    formSetup: PropTypes.shape({}),
    company: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  qualityTest: PropTypes.shape({
    testSuccess: PropTypes.bool,
    testing: PropTypes.bool,
    results: PropTypes.shape({
      success: PropTypes.bool,
    }),
    audio: PropTypes.shape({}),
    video: PropTypes.shape({}),
  }).isRequired,
  networkTest: PropTypes.shape({
    results: PropTypes.shape({
      success: PropTypes.bool,
    }),
    success: PropTypes.bool,
    testing: PropTypes.bool,
    failedTests: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  apiKey: PropTypes.string,
  sessionId: PropTypes.string,
  token: PropTypes.string,
  candidate: PropTypes.shape({
    uuid: PropTypes.string,
    _id: PropTypes.string,
    state: PropTypes.string,
  }).isRequired,
  errorType: PropTypes.string,
  session: PropTypes.shape({}),
  websocketError: PropTypes.string,
  openTokError: PropTypes.string,
}

const defaultProps = {
  errorType: '',
  session: undefined,
  apiKey: '',
  sessionId: '',
  token: '',
  websocketError: '',
  openTokError: '',
}

const mapStateToProps = state => ({
  apiKey: state.global.opentok.apiKey,
  sessionId: state.global.opentok.testSessionData.sessionId,
  token: state.global.opentok.testSessionData.token,
  qualityTest: state.interview.sequencedInterview.qualityTest,
  networkTest: state.interview.sequencedInterview.networkTest,
  errorType: state.interview.sequencedInterview.errorType,
  openTokError: state.global.opentok.error,
  jobOpening: state.interview.jobOpening,
  candidate: state.candidate,
  session: state.global.opentok.session,
  candidateState: state.interview.candidate.state,
  websocketError: state.global.websocketError,
})

const StreamContainer = styled.div`
  max-width: 50rem; // to get the same width as the results
  margin: 0 auto;

  ${({ theme }) => theme.mediaQuery.tablet} {
    max-width: 100%;
  }
`
export class NetworkTest extends Component {
  constructor(props) {
    super(props)

    this.state = {
      askConfirmation: false,
      skipQualityTest: false,
      onIe: false,
      iosUnsupported: false,
    }

    this.testContainerRef = React.createRef()

    this.onConfirm = this.onConfirm.bind(this)
    this.startTest = this.startTest.bind(this)
    this.startInterview = this.startInterview.bind(this)
  }

  componentDidMount() {
    const { dispatch } = this.props
    this.detectDeviceAndBrowser()
    dispatch(authenticateSession())
  }

  componentDidUpdate(prevProps) {
    const {
      candidate: { uuid },
      dispatch,
      networkTest,
      qualityTest,
      session,
      errorType,
      websocketError,
    } = this.props
    const { skipQualityTest } = this.state

    const testSuccess =
      networkTest.results &&
      networkTest.results.success &&
      qualityTest.testSuccess &&
      !errorType

    const successWithoutQualityTest =
      skipQualityTest && networkTest.results && networkTest.results.success

    if ((testSuccess || successWithoutQualityTest) && !session) {
      dispatch(authenticateSession(uuid))
    }

    if (websocketError !== prevProps.websocketError) {
      if (websocketError.length > 0 && !testSuccess) {
        // set testing to false in state when websocket error occurs
        // and update error to reconnecting to render error message
        dispatch(updateNetworkTestData())
        dispatch(updateQualityTestData())
        dispatch(updateError('reconnecting'))
      } else {
        dispatch(updateError(''))
      }
    }
  }

  componentWillUnmount() {
    const { session, dispatch } = this.props

    if (session && session.currentState === 'connected') {
      dispatch(disconnectSession())
    }
  }

  onConfirm() {
    this.setState({ askConfirmation: false }, () => this.onSubmit())
  }

  onSubmit() {
    const {
      dispatch,
      candidate,
      jobOpening,
    } = this.props
    // pass testSuccess: true to the updateState when moving on to the interview
    const candidateInfo = getCandidateInfoForAmplitude(candidate)
    const companyAndInterviewInfo = getCompanyAndInterviewInfoForAmplitude(jobOpening)
    
    analytics.onClickEvent({
      locationId: 'timedInterviewPage',
      id: 'confirmstartInterview',
      properties: {
        ...candidateInfo,
        ...companyAndInterviewInfo,
      },
    })

    dispatch(updateState(candidate.uuid, undefined, false, false, true))
  }

  detectDeviceAndBrowser() {
    const { userAgent } = navigator
    const { dispatch } = this.props
    if (userAgent.match(/edge/i)) {
      this.setState({ skipQualityTest: true })
    } else if (userAgent.match(/msie|trident/i)) {
      this.setState({ onIe: true })
      dispatch(updateError('unsupportedBrowser'))
    } else if (userAgent.match(/crios|fxios/i)) {
      this.setState({ iosUnsupported: true })
      dispatch(updateError('unsupportedBrowser'))
    } else {
      dispatch(updateError(''))
    }
  }

  startTest() {
    const {
      dispatch,
      candidate: { uuid },
      apiKey,
      sessionId,
      token,
    } = this.props
    const testSessionData = { apiKey, sessionId, token }
    dispatch(startTest(testSessionData, uuid))
    if (this.testContainerRef.current) {
      this.testContainerRef.current.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  startInterview() {
    const { jobOpening, candidate } = this.props
    const candidateInfo = getCandidateInfoForAmplitude(candidate)
    const companyAndInterviewInfo = getCompanyAndInterviewInfoForAmplitude(jobOpening)

    this.setState({ askConfirmation: true })

    analytics.onModalLoadEvent({
      locationId: 'timedInterviewPage',
      id: 'confirmStartInterview',
      properties: {
        candidateId: candidate._id,
        ...candidateInfo,
        ...companyAndInterviewInfo,
      },
    })
  }

  renderErrorMessage() {
    const { i18n, openTokError } = this.props
    const { skipQualityTest, onIe, iosUnsupported } = this.state
    const alertVariant = skipQualityTest ? 'info' : 'error'

    return (
      <TimedInterviewError
        testing
        onIe={onIe}
        openTokError={!!openTokError}
        iosUnsupported={iosUnsupported}
        variant={alertVariant}
        customMessage={
          skipQualityTest
            ? i18n.sequencedInterview.browserInfo.qualityTestNotSupported
            : ''
        }
      />
    )
  }

  renderButtonsAndStream() {
    const {
      networkTest,
      qualityTest,
      errorType,
      websocketError,
      i18n,
      apiKey,
      sessionId,
      candidate,
      jobOpening,
      token,
    } = this.props
    const { skipQualityTest, onIe, iosUnsupported } = this.state
    const candidateInfo = getCandidateInfoForAmplitude(candidate)
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(jobOpening)

    const testSuccess =
      networkTest.results &&
      networkTest.results.success &&
      qualityTest.testSuccess &&
      !errorType

    const successWithoutQualityTest =
      skipQualityTest && networkTest.results && networkTest.results.success

    const hasTestSessionData = apiKey && sessionId && token
    const buttonText = !networkTest.results
      ? i18n.sequencedInterview.networkTest.startTest
      : i18n.sequencedInterview.networkTest.testAgain

    if (
      onIe ||
      iosUnsupported ||
      (errorType && errorType === 'unsupportedBrowser') ||
      networkTest.testing ||
      (qualityTest.testing && !skipQualityTest)
    ) {
      return null
    }

    if (testSuccess || successWithoutQualityTest) {
      return (
        <StreamContainer>
          <StreamView testing />
          <Flex mt="s32" justifyContent="center">
            <Button
              iconRight={jobOpening.language === 'ar' ? 'west' : 'east'}
              onClick={this.startInterview}
              text={i18n.sequencedInterview.networkTest.startInterview}
            />
          </Flex>
        </StreamContainer>
      )
    }

    return (
      <Flex justifyContent={!networkTest.results ? 'left' : 'center'}>
        <Button
          onClick={this.startTest}
          text={buttonText}
          icon="network_check"
          disabled={!hasTestSessionData || websocketError}
          event={{
            locationId: 'timedInterviewPage',
            id: 'startNetworkTestButton',
            properties: {
              ...candidateInfo,
              ...companyAndInterviewInfo,
            },
          }}
        />
      </Flex>
    )
  }

  renderBrowserInfo() {
    const { i18n } = this.props
    const browserInfoKeys = i18n.sequencedInterview.browserInfo
    return (
      <Box mt="s16">
        <Subtitle>{browserInfoKeys.browserSupportTitle}</Subtitle>
        <Body>{browserInfoKeys.supportedBrowsers}</Body>
        <Subtitle>{browserInfoKeys.mobileSupported}</Subtitle>
        <Body>{browserInfoKeys.android}</Body>
        <Body>{browserInfoKeys.iOS}</Body>
        <Body fontWeight={600}>{browserInfoKeys.latestVersion}</Body>
        <Body fontWeight={600}>{browserInfoKeys.freezeInstruction}</Body>
      </Box>
    )
  }

  render() {
    const { i18n, errorType, jobOpening } = this.props
    const { askConfirmation, skipQualityTest } = this.state

    const confirmContent = (
      <>
        <Body>{i18n.sequencedInterview.networkTest.confirmWarning}</Body>
        <Body>{i18n.sequencedInterview.networkTest.confirmText}</Body>
        <Body>{i18n.sequencedInterview.networkTest.confirmText2}</Body>
        <Body fontWeight={600}>
          {i18n.sequencedInterview.networkTest.confirmText3}
        </Body>
        <Body>{i18n.sequencedInterview.networkTest.goodLuck}</Body>
      </>
    )

    return (
      <TimedInterviewWrapper>
        <H3>{i18n.sequencedInterview.networkTest.header}</H3>
        <Body>{i18n.sequencedInterview.networkTest.description}</Body>
        {this.renderBrowserInfo()}
        {this.renderErrorMessage()}
        <div ref={this.testContainerRef}>
          {!errorType && (
            <NetworkTestResults skipQualityTest={skipQualityTest} />
          )}
        </div>
        {this.renderButtonsAndStream()}
        <Confirmation
          size="medium"
          id="timed-interview-continue-confirm"
          flippedView={jobOpening.language === 'ar'}
          isOpen={askConfirmation}
          title={i18n.sequencedInterview.networkTest.confirmTitle}
          body={confirmContent}
          onCancel={() => this.setState({ askConfirmation: false })}
          onConfirm={this.onConfirm}
          useConfirm
          primaryButtonText={i18n.sequencedInterview.networkTest.startInterview}
        />
      </TimedInterviewWrapper>
    )
  }
}

NetworkTest.propTypes = propTypes
NetworkTest.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  NetworkTest
)
